import * as React from 'react';
import { useId } from '@sail/react-aria';
import { createView, view, dynamic, createViewConfig } from '@sail/engine';
import { Description, ErrorText } from './FormField';
import { NativeTextFieldConfig } from './TextField';
import { NativeSelectConfig } from './Select';
import { NativeTextAreaConfig } from './TextArea';
import { CompositeFieldConfig } from './CompositeField';
import { Label } from './Label';
import { css } from '../css';
export const FormFieldGroupContext = /*#__PURE__*/ React.createContext({
    elements: {
        description: {},
        legend: {}
    }
});
export const InputWrapperConfig = createViewConfig({
    props: {},
    name: 'InputWrapper',
    flattens: true
});
const InputWrapper = InputWrapperConfig.createView(// "invalid" is presumably exposed to enable subview customization based on the validity of the field
// but we shouldn't spread it onto the DOM element.
({ invalid: _omit, ...props })=>/*#__PURE__*/ React.createElement("div", props));
// The error block goes above the group if it's a vertical layout, like address field
// and below the group if it's a horizontal layout, like currency field.
const ErrorBlock = createView(({ error, errorId, isHidden, ...props })=>/*#__PURE__*/ React.createElement(view.div, {
        role: "alert",
        ...props
    }, error ? /*#__PURE__*/ React.createElement(ErrorText, {
        role: "none",
        id: errorId,
        hidden: isHidden
    }, error) : null));
export const FormFieldGroupConfig = createViewConfig({
    props: {},
    name: 'FormFieldGroup',
    flattens: true,
    defaults: {
        size: 'medium',
        layout: 'horizontal',
        hiddenElements: []
    }
});
/**
 * @external
 * @param name
 * @param form
 */ export const FormFieldGroup = FormFieldGroupConfig.createView(({ layout, legend, disabled, error, description, children, size, hiddenElements, subviews, ...props })=>{
    const ref = React.useRef(null);
    const legendId = useId();
    const descriptionId = useId();
    const errorId = props['aria-describedby'] || useId();
    const errorProps = {};
    if (error) {
        errorProps['aria-describedby'] = errorId;
        errorProps['aria-invalid'] = true;
    }
    const handleClick = ()=>{
        var _ref_current_querySelector, _ref_current;
        (_ref_current = ref.current) === null || _ref_current === void 0 ? void 0 : (_ref_current_querySelector = _ref_current.querySelector('select,button,input:not([disabled])')) === null || _ref_current_querySelector === void 0 ? void 0 : _ref_current_querySelector.focus();
    };
    return /*#__PURE__*/ React.createElement(view.fieldset, {
        ...props,
        ...errorProps,
        ref: ref
    }, /*#__PURE__*/ React.createElement(FormFieldGroupContext.Provider, {
        value: {
            elements: {
                description: {
                    id: descriptionId,
                    present: Boolean(description)
                },
                legend: {
                    id: legendId,
                    present: Boolean(legend)
                }
            },
            disabled
        }
    }, /*#__PURE__*/ React.createElement(Label, {
        hidden: hiddenElements === null || hiddenElements === void 0 ? void 0 : hiddenElements.includes('legend'),
        as: "legend",
        onClick: handleClick,
        id: legendId,
        inherits: subviews.label
    }, legend), description ? /*#__PURE__*/ React.createElement(Description, {
        hidden: hiddenElements === null || hiddenElements === void 0 ? void 0 : hiddenElements.includes('description'),
        id: descriptionId,
        css: legend ? css({
            bleedTop: 'xsmall'
        }) : undefined,
        inherits: subviews.description
    }, description) : null, layout !== 'horizontal' ? /*#__PURE__*/ React.createElement(ErrorBlock, {
        inherits: subviews.error,
        error: error,
        errorId: errorId,
        isHidden: hiddenElements === null || hiddenElements === void 0 ? void 0 : hiddenElements.includes('error')
    }) : null, /*#__PURE__*/ React.createElement(InputWrapper, {
        inherits: subviews.inputs,
        disabled: disabled,
        invalid: !!error,
        css: layout === 'vertical' ? css({
            stack: 'y'
        }) : css({
            stack: 'x'
        })
    }, children), layout === 'horizontal' ? /*#__PURE__*/ React.createElement(ErrorBlock, {
        inherits: subviews.error,
        error: error,
        errorId: errorId,
        isHidden: hiddenElements === null || hiddenElements === void 0 ? void 0 : hiddenElements.includes('error')
    }) : null));
}, {
    css: {
        stack: 'y',
        gap: 'xsmall'
    },
    subviews: {
        // NOTE: gap doesn't apply to `<legend>` elements (without `display: contents`)
        // see: https://drafts.csswg.org/css-display/#unbox-html
        label: {
            uses: [
                dynamic(({ hidden })=>{
                    if (!hidden) {
                        return [
                            css({
                                display: 'contents'
                            })
                        ];
                    }
                })
            ]
        },
        inputs: {
            css: {
                gap: 'small'
            }
        },
        // empty error container needs to be present for the error message to be read
        // by screen readers on insertion, but we don't want it to participate in the layout.
        error: {
            uses: [
                css({
                    display: 'contents'
                })
            ]
        }
    },
    variants: {
        disabled: {
            true: {
                subviews: {
                    label: {
                        css: {
                            color: 'form.disabled'
                        }
                    },
                    description: {
                        css: {
                            color: 'form.disabled'
                        }
                    }
                }
            }
        }
    },
    forward: {
        disabled: true
    },
    uses: [
        dynamic(({ size, disabled })=>[
                NativeSelectConfig,
                NativeTextAreaConfig,
                NativeTextFieldConfig,
                CompositeFieldConfig
            ].map((context)=>context.customize({
                    defaults: {
                        size,
                        disabled
                    }
                })))
    ]
});

//# sourceMappingURL=FormFieldGroup.js.map